import { observable, action, computed } from 'mobx';
import isNull from 'lodash/isNull';
import zip from 'lodash/zip';
import isUndefined from 'lodash/isUndefined';
import range from 'lodash/range';
import omit from 'lodash/omit';
import last from 'lodash/last';
import sum from 'lodash/sum';
import set from 'lodash/set';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { ChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series';

import { parseValueWithStyle } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/reverseFormat/reverseFormat';
import { fmtIsDate } from '@tencent/tdocs-format-render';
import type IParsedValue from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/reverseFormat/ParsedValue';
import { BubbleChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series/bubble-chart-series';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ISubChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import {
    getChartSeriesConstructorByType,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series/series-factory';
import { SubChartType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import { ISubChartSerData } from '@tencent/slide-sdk-model';
import {
    IBubbleChartSeries,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/bubble-chart.interface';
import { IBaseSubChartProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import { IChartSeries } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import { getFormattedNumber } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/format';

export default class ChartManager {
    @observable public editingChartModel: Record<string, any> | null = null;
    @observable public isShowChartEditModal = false;
    public chartTableData: Record<string, any> = {};
    public subChartSeriesLength: number[] = [];
    public isEdited = false;
    public seriesInfos: (Record<string, any> | undefined)[] = [];
    public spreadsheetInstance: Record<string, any> | null = null;

    @computed public get chartData(): Record<string, any>[] {
        return this.editingChartModel?.plotArea?.charts || [];
    }

    @computed public get AllChartSeries(): Record<string, any>[] {
        const allChartSeries: Record<string, any>[] = [];

        if (isNull(this.editingChartModel)) {
            return allChartSeries;
        }

        this.editingChartModel.plotArea.charts.forEach((subChart) => {
            allChartSeries.push(...subChart.series);
        });

        return allChartSeries;
    }

    public updateSpreadsheetInstance(spreadsheet: Record<string, any>): void {
        this.spreadsheetInstance = spreadsheet;
    }

    public updateIsEdited(flag: boolean): void {
        this.isEdited = flag;
    }

    public unformatValue(originValue: string): IParsedValue['value'] {
        const { value, numberFormat } = parseValueWithStyle(originValue);

        const isDate = fmtIsDate(numberFormat.formatCode || '');

        if (isDate && Number(value) > 60) {
            // 格式为日期时，office中value为60为1900.2.29，实际并没有
            return String(Number(value) + 1);
        }

        return value;
    }

    public getRowEntries(rows: Record<string, any>): [string, any][] {
        return Object.entries(rows).filter(([rowIndex]) => /\d+/.test(rowIndex));
    }

    public fillInBlankCells(rows: Record<string, any>): {
        totalRowLength: number;
        totalColumnLength: number;
    } {
        let totalRowLength = 0;
        let totalColumnLength = 0;
        const rowEntries = this.getRowEntries(rows);

        rowEntries.forEach(([, rowData]: [string, Record<string, any>]) => {
            const lastColumnIndex = last(Object.keys(rowData.cells));

            if (Number(lastColumnIndex) > totalColumnLength) {
                totalColumnLength = Number(lastColumnIndex);
            }
        });

        if (isUndefined(last(rowEntries))) {
            return {
                totalRowLength: 0,
                totalColumnLength: 0,
            };
        }

        // @ts-ignore
        totalRowLength = Number(last(rowEntries)[0]);

        // 补齐所有空元素
        range(totalRowLength + 1).forEach((rowIndex) => {
            // 没有该行号
            if (isUndefined(rows[rowIndex])) {
                rows[rowIndex] = {
                    cells: {},
                };

                range(totalColumnLength + 1).forEach((columnIndex) => {
                    // 左上角不赋值
                    if (rowIndex === 0 && columnIndex === 0) {
                        return;
                    }

                    rows[rowIndex].cells[columnIndex] = {
                        text: '',
                    };
                });
            } else {
                range(totalColumnLength + 1).forEach((columnIndex) => {
                    // 左上角不赋值
                    if (rowIndex === 0 && columnIndex === 0) {
                        return;
                    }

                    // 该列没有值
                    if (isUndefined(rows[rowIndex].cells?.[columnIndex])) {
                        rows[rowIndex].cells[columnIndex] = {
                            text: '',
                        };
                    }
                });
            }
        });

        const newEntries = this.getRowEntries(rows);

        if (newEntries.length !== 0) {
            this.seriesInfos = range(totalColumnLength).map(() => undefined);

            // 获取列的 seriesInfo，确保删除任意行都能获取列的 seriesInfo
            for (let rowIndex = 0; rowIndex < totalRowLength; ++rowIndex) {
                for (let columnIndex = 1; columnIndex < totalColumnLength + 1; ++columnIndex) {
                    const currentSeriesInfo = rows[rowIndex].cells[columnIndex].seriesInfo;

                    // 注意如果有复制操作会把原的 cell 的字段例如 seriesInfo 拷贝，但奇怪的是 seriesInfo 表格没被拷贝原型
                    // 所以要判断这个 currentSeriesInfo 是不是继承自 ChartSeries
                    // 但是目前 spreadsheet 在撤销和拷贝的过程中经常丢失 seriesInfo 的构造函数相关信息使得其继承自 object
                    // 下面在生成新 series 的时候需要有相关处理
                    if (
                        !isUndefined(currentSeriesInfo)
                        && isUndefined(this.seriesInfos[columnIndex - 1])
                        // && currentSeriesInfo instanceof ChartSeries
                    ) {
                        this.seriesInfos[columnIndex - 1] = currentSeriesInfo;
                    }
                }

                if (this.seriesInfos.every(seriesInfo => !isUndefined(seriesInfo))) {
                    break;
                }
            }
        }

        return {
            totalRowLength,
            totalColumnLength,
        };
    }

    // 生成的 model 数据，列的 id 重新排列，名称若有重复则修改为不同的
    public handleSeriesIdAndName(rows: Record<string, any>): void {
        const seriesNameSets = this.subChartSeriesLength.map(() => new Set<string>());

        this.AllChartSeries.forEach((series, seriesIndex) => {
            series.originIdx = series.idx;
            series.idx = seriesIndex;
            series.order = seriesIndex;

            const { currentSubChartIndex } = this.getEditingSubChartIndexes(Number(seriesIndex + 1));
            const currentSeriesNameSet = seriesNameSets[currentSubChartIndex];

            // office 中不允许列名为空字符串，这里自行填值
            if (series.name === '') {
                /* eslint-disable-next-line */
                series.name = `列${String(seriesIndex + 1)}`;
                rows[0].cells[seriesIndex + 1].text = series.name;
            }

            if (currentSeriesNameSet.has(series.name)) {
                /* eslint-disable-next-line */
                let newColumnName = `列${String(seriesIndex + 1)}`;

                if (currentSeriesNameSet.has(newColumnName)) {
                    newColumnName = `${newColumnName}${seriesIndex + 1}`;
                }

                series.name = newColumnName;
                rows[0].cells[seriesIndex + 1].text = newColumnName;
            }

            currentSeriesNameSet.add(series.name);
        });
    }

    public handleTableArea(
        spreadsheetInstance: Record<string, any>,
        totalLens: {
            row: number;
            column: number;
        },
    ): void {
        const { row: totalRowLength, column: totalColumnLength } = totalLens;

        if (totalRowLength + 1 <= 20) {
            spreadsheetInstance.datas[0].rows.len = 20;
        } else {
            spreadsheetInstance.datas[0].rows.len = totalRowLength + 1;
        }

        if (totalColumnLength + 1 <= 7) {
            // cols.len = 7;
            spreadsheetInstance.datas[0].cols.len = 7;
        } else {
            // cols.len = totalColumnLength + 1;
            spreadsheetInstance.datas[0].cols.len = totalColumnLength + 1;
        }
    }

    public convertAndSendTableDataToSdk(
        tableData: Record<string, any>,
        spreadsheetInstance: Record<string, any>,
    ): void {
        // spreadsheet 只有一张表
        const { rows } = tableData[0];
        const rowTitles: (string | undefined)[] = [];
        const isBubbleChart = this.chartData?.[0]?.type === SubChartType.BubbleChart;

        // 判断内容区的边界并给未有索引的单元格赋值
        const { totalRowLength, totalColumnLength } = this.fillInBlankCells(rows);
        const newRowEntries = this.getRowEntries(rows);

        newRowEntries.forEach(([rowIndex, rowData]: [string, Record<string, any>]) => {
            if (Number(rowIndex) !== 0) {
                rowTitles[Number(rowIndex) - 1] = rowData.cells['0']?.text ?? '';
            }
        });

        // 更新subChartSeriesLength
        this.subChartSeriesLength = this.chartData.map((subChart: Record<string, any>) => subChart.series.length);

        // 先清空所有的 dataList
        this.chartData?.forEach?.((subChartData) => {
            subChartData.series = [];
        });

        // 下面dataList被清空了，这里存一个，下面有逻辑需要判定
        const preDataList: any[] = [];
        /* eslint-disable */ // eslint --fix 的 bug
        newRowEntries.forEach(([rowIndex, rowData]: [string, any]) => {
            // 第一行：列名称
            if (rowIndex === '0') {
                Object.entries(rowData.cells).forEach(([columnIndex, cellContent]: [string, Record<string, any>]) => {
                    const columnIdx = Number(columnIndex);
                    const seriesIdx = columnIdx - 1;
                    // 如果直接删除了第一行，columnIndex 可能会为 0，忽略
                    if (columnIdx === 0) {
                        return;
                    }

                    // 气泡图不读取 bubbleSize 的列来生成 series
                    if (isBubbleChart && (columnIdx) % 2 === 0) {
                        return;
                    }

                    // 如果该列的索引是上一列的索引 + 1
                    const { currentSubChartIndex, currentSubChartSeriesIndex } = this.getEditingSubChartIndexes(
                        // 气泡图读取奇数列
                        !isBubbleChart ? columnIdx : (columnIdx + 1) / 2,
                    );
                    let seriesInfo = cloneDeep(this.seriesInfos[seriesIdx]);

                    if (!isUndefined(seriesInfo)) {
                        if (
                            !(seriesInfo instanceof ChartSeries)
                            && Object.prototype.hasOwnProperty.call(seriesInfo, 'dataList')
                            && Object.prototype.hasOwnProperty.call(seriesInfo, 'name')
                        ) {
                            const subChartSeriesConstuctor = getChartSeriesConstructorByType(
                                this.chartData[currentSubChartIndex]?.type,
                            );

                            if (isUndefined(subChartSeriesConstuctor)) {
                                return;
                            }

                            let subChartConstuctorParams: Record<string, any> = {
                                idx: sum(this.subChartSeriesLength),
                                order: sum(this.subChartSeriesLength),
                                seriesText: {
                                    value: seriesInfo.name,
                                },
                                categories: rowTitles.map((rowTitle, rowIndex) => ({
                                    categoryPoint: {
                                        value: rowTitle,
                                    },
                                    valuePoint: {
                                        // 如果先写了列名，其它行该列的值都为空
                                        value: rows[Number(rowIndex) + 1].cells[columnIdx]?.text ?? '',
                                    },
                                })),
                            }

                            if (this.chartData[currentSubChartIndex]?.type === SubChartType.BubbleChart) {
                                subChartConstuctorParams = {
                                    ...subChartConstuctorParams,
                                    bubbleSize: BubbleChartSeries.getSdkBubbleSize(
                                        rowTitles.map((_, rowIndex) =>
                                            rows[Number(rowIndex) + 1].cells[columnIdx + 1]?.text ?? ''
                                        )
                                    ),
                                }
                            }

                            seriesInfo = new subChartSeriesConstuctor(
                                subChartConstuctorParams as ISubChartSerData,
                                {
                                    categoryPointList: rowTitles.map((rowTitle) => ({
                                        value: !isUndefined(rowTitle) ? rowTitle : '',
                                        formatCode: this.chartData[currentSubChartIndex].categoryFormatCode,
                                    })),
                                    count: rowTitles.length,
                                }
                            );
                        }

                        if (isUndefined(cellContent.seriesInfo)) {
                            cellContent.seriesInfo = seriesInfo;
                        }

                        preDataList.push(seriesInfo.dataList);
                        seriesInfo.dataList = [];
                        // 删除的时候，cellContent.text是undefined，会导致在后面的handleSeriesIdAndName不处理空值补全
                        // 这里改成没有内容就为''，让后续补充为“列n”
                        seriesInfo.name = cellContent.text || '';
                        if (this.chartData[currentSubChartIndex]?.series) {
                            this.chartData[currentSubChartIndex].series[currentSubChartSeriesIndex] = seriesInfo;
                        }
                    } else {
                        // 第一行 新增列
                        const subChartSeriesConstuctor = getChartSeriesConstructorByType(
                            this.chartData[currentSubChartIndex]?.type,
                        );

                        if (isUndefined(subChartSeriesConstuctor)) {
                            return;
                        }

                        let serConstuctorParams: Record<string, any> = {
                            idx: seriesIdx,
                            order: seriesIdx,
                            seriesText: {
                                // 第一行才会走到这里，按目前逻辑是 列名 那一行，所以这里需要赋值本文内容，否则在新增系列的时候，会无法修改图例名称
                                value: cellContent.text || '',
                            },
                            categories: rowTitles.map((_, rowIndex) => ({
                                categoryPoint: {
                                    // TODO 这里的赋值逻辑其实很奇怪，cellContent.text逻辑上不是类别名称，后续后重新赋值了一次。但是先不改动，怕有问题。后续整个需要重构
                                    value: cellContent.text,
                                },
                                valuePoint: {
                                    // 如果先写了列名，其它行该列的值都为空
                                    value: rows[Number(rowIndex) + 1].cells[columnIdx]?.text ?? '',
                                },
                            })),

                        }

                        if (this.chartData[currentSubChartIndex].type === SubChartType.BubbleChart) {
                            serConstuctorParams = {
                                ...serConstuctorParams,
                                bubbleSize: BubbleChartSeries.getSdkBubbleSize(
                                    rowTitles.map((_, rowIndex) =>
                                        rows[Number(rowIndex) + 1].cells[columnIdx + 1]?.text ?? ''
                                    )
                                ),
                            }
                        }

                        const emptySubChartSeries = new subChartSeriesConstuctor(
                            serConstuctorParams as ISubChartSerData,
                            {
                                categoryPointList: rowTitles.map((rowTitle) => ({
                                    value: !isUndefined(rowTitle) ? rowTitle : '',
                                    formatCode: this.chartData[currentSubChartIndex].categoryFormatCode,
                                })),
                                count: rowTitles.length,
                            }
                        );


                        const chartSeries = this.chartData[currentSubChartIndex]?.series;
                        if (chartSeries) {
                            chartSeries[currentSubChartSeriesIndex] = emptySubChartSeries;
                        }

                        this.seriesInfos[columnIdx - 1] = emptySubChartSeries;
                        // 创建 series name 有空格为图表自身兼容客户端的逻辑限制，web 编辑不受影响可以直接去掉
                        emptySubChartSeries.name = emptySubChartSeries.name.trimEnd();

                        const originSeries = this.seriesInfos.find(i => !!i);

                        // 新系列需要继承一部分原有系列的属性
                        emptySubChartSeries.categoryFormatCode = emptySubChartSeries.categoryFormatCode ?? originSeries?.categoryFormatCode;
                        emptySubChartSeries.categoryType = emptySubChartSeries.categoryType ?? originSeries?.categoryType;
                        emptySubChartSeries.valueFormatCode = emptySubChartSeries.valueFormatCode ?? originSeries?.valueFormatCode;
                        emptySubChartSeries.valueType = emptySubChartSeries.valueType ?? originSeries?.valueType;
                        emptySubChartSeries.dataLabels = emptySubChartSeries.dataLabels ?? originSeries?.dataLabels;

                        // 新增的时候把props的属性用主题赋值，不然导出后和office不一致
                        const subChartProperty = this.chartData[currentSubChartIndex].subChartProperty as IBaseSubChartProperty;
                        const colorPaletteId = this.editingChartModel?.chartSpace?.colorStyle?.id;
                        const totalSeriesCount = this.AllChartSeries.length + 1;
                        emptySubChartSeries.props = {
                            style: subChartProperty.getDefaultThemeStyle(originSeries as IChartSeries, seriesIdx, colorPaletteId, totalSeriesCount),
                        }
                        const serMarker = emptySubChartSeries.marker ?? originSeries?.marker;
                        if (serMarker) {
                            emptySubChartSeries.marker = {
                                ...serMarker,
                                props: {
                                    style: subChartProperty.getDefaultMarkerThemeStyle(seriesIdx, colorPaletteId, totalSeriesCount),
                                }
                            };
                        }

                        cellContent.text = emptySubChartSeries.name;
                        cellContent.seriesInfo = emptySubChartSeries;
                    }
                });
            } else {
                // 其它行
                Object.entries(rowData.cells).forEach(([columnIndex, cellContent]: [string, Record<string, any>]) => {
                    if (/\d+/.test(columnIndex)) {
                        const columnIdx  = Number(columnIndex);
                        if (columnIdx === 0) {
                            this.chartData.forEach((subChartData) => {
                                subChartData.series?.forEach((series, index) => {
                                    const categoryIndex = Number(rowIndex) - 1;
                                    const preDataListInfo = preDataList[index];
                                    const formatCode = preDataListInfo?.[categoryIndex]?.categoryPoint?.formatCode || series.categoryFormatCode;
                                    series.dataList[categoryIndex] = {
                                        categoryPoint: {
                                            value: isUndefined(formatCode)
                                                ? cellContent.text
                                                : this.unformatValue(cellContent.text),
                                        },
                                    };
                                });
                            });
                        } else {
                            const { currentSubChartIndex, currentSubChartSeriesIndex } = this.getEditingSubChartIndexes(
                                // 偶数列和前面的奇数列共用同一个 series
                                !isBubbleChart ? columnIdx : Math.floor((columnIdx + 1) / 2),
                            );

                            // 上个代码块集体赋值了所有子表的每行的行名，由于用户可能会修改，所以这里需要同步
                            // 这里存在一种场景是用户下拉某列复制生成数据，新生成的行其它列的值为空
                            const realCategoryValue = get(this.chartData, `[0].series[0].dataList.[${Number(rowIndex) - 1}].categoryPoint.value`);
                            const seriesInfo = this.seriesInfos[columnIdx - 1];
                            set(cellContent, 'categoryPoint.value', realCategoryValue ?? '');
                            // 同步表格显示的值和 model 存储的值
                            set(
                                cellContent,
                                'valuePoint.value',
                                isUndefined(seriesInfo?.valueFormatCode)
                                    ? cellContent.text
                                    : this.unformatValue(cellContent.text),
                            );

                            // 补上 seriesInfo
                            if (isUndefined(cellContent.seriesInfo)) {
                                cellContent.seriesInfo = this.seriesInfos[columnIdx - 1];
                            }

                            if (isBubbleChart && columnIdx % 2 === 0) {
                                const currentSeries = this.chartData[currentSubChartIndex]?.series?.[currentSubChartSeriesIndex];
                                currentSeries && set(
                                    currentSeries,
                                    `bubbleSize[${Number(rowIndex) - 1}]`,
                                    cellContent.text
                                );
                            } else {
                                // 气泡图最后一列是奇数，bubbleSize 需要填充为 1
                                // 此为 office 的 ooxml 的默认行为，但是 office 的编辑里可能不显示最后这列默认填充的值
                                if (isBubbleChart && columnIdx % 2 !== 0 && columnIdx === this.seriesInfos.length) {
                                    const currentSeries = this.chartData[currentSubChartIndex]?.series?.[currentSubChartSeriesIndex];
                                    currentSeries && set(
                                        currentSeries,
                                        `bubbleSize`,
                                        range(this.AllChartSeries.length).map(() => '1'),
                                    );
                                }

                                const dataList = this.chartData[currentSubChartIndex]?.series?.[currentSubChartSeriesIndex]?.dataList;
                                if (dataList) {
                                    dataList[Number(rowIndex) - 1] = omit(cellContent, ['text', 'seriesInfo']);
                                }
                            }
                        }
                    }
                });
            }
        });

        this.handleSeriesIdAndName(rows);
        this.handleTableArea(spreadsheetInstance, {
            row: totalRowLength,
            column: totalColumnLength,
        });
        this.updateChartSDKData();
    }

    @action
    public addChartModel(chartModel): void {
        this.editingChartModel = chartModel;
        this.chartTableData = this.convertChartDataFromSDK();
        this.subChartSeriesLength = this.chartData.map((subChart: Record<string, any>) => subChart.series?.length ?? 0);
    }

    @action
    switchShowChartEditModal(flag: boolean) {
        this.isShowChartEditModal = flag;
    }

    @action
    public clearChartModel(): void {
        this.editingChartModel = null;
        this.isShowChartEditModal = false;
        this.chartTableData = {};
        this.subChartSeriesLength = [];
        this.isEdited = false;
        this.seriesInfos = [];
        this.spreadsheetInstance?.unBindGlobalEvents?.();
        this.spreadsheetInstance = null;
    }

    @action
    public convertChartDataFromSDK(): Record<string, any> {
        if (isNull(this.editingChartModel)) {
            throw new Error('Accident manipulate chart data before double click chart');
        }

        const chartDatas = this.editingChartModel.plotArea.charts;
        const firstSeries = chartDatas[0].series[0] as ChartSeries;
        let isBubbleChart = chartDatas[0].type === SubChartType.BubbleChart;

        // 没有数据返回个默认表格数据
        if (isUndefined(firstSeries)) {
            return {
                len: 20,
                0: {
                    cells: {},
                },
            };
        }

        const { categoryFormatCode: formatCode } = firstSeries;

        const datas = firstSeries.dataList.map((dataItem) => {
            return {
                value: dataItem.categoryPoint?.value,
                formatCode: dataItem.categoryPoint?.formatCode || formatCode
            }
        });

        const rowTitles: string[] = datas.map(({ value, formatCode }) => getFormattedNumber(value, formatCode))
        const columnTitles: string[] = [];
        const values: any[] = [];

        chartDatas.forEach((subChartData) => {
            subChartData?.series?.forEach((series: ChartSeries, seriesIndex) => {
                columnTitles.push(series.name);
                values.push(series.dataList);

                // 气泡图遍历每个 series 需要额外添加一列
                if (isBubbleChart) {
                    columnTitles.push(`大小${seriesIndex === 0 ? '' : seriesIndex + 1}`);
                    values.push(
                        (series as IBubbleChartSeries).bubbleSize?.map((size, sizeIndex) => ({
                            categoryPoint: { value: series.dataList[sizeIndex].categoryPoint?.value },
                            valuePoint: { value: size },
                        })),
                    );
                }
            });
        });

        // 矩阵变换，将每个元素代表的列值集合转换为行值的集合
        const zippedValues = zip(...values);

        const chartTableData = {
            len: rowTitles.length + 1 >= 20 ? rowTitles.length + 1 : 20,
            0: {
                cells: {},
            },
        };

        columnTitles.forEach((title, columnIndex) => {
            chartTableData[0].cells[columnIndex + 1] = {
                text: title || '',
                seriesInfo: !isBubbleChart
                    ? this.AllChartSeries[columnIndex]
                    : this.AllChartSeries[Math.floor(columnIndex / 2)],
            };
        });

        rowTitles.forEach((title, rowIndex) => {
            if (!chartTableData[rowIndex + 1]) {
                chartTableData[rowIndex + 1] = { cells: { 0: { text: title || '' } } };
            }

            zippedValues[rowIndex].forEach((value: any, valueIndex) => {
                const convertedValue = value?.valuePoint?.value;

                chartTableData[rowIndex + 1].cells[valueIndex + 1] = {
                    text:
                        !isUndefined(convertedValue) && convertedValue !== ''
                            ? String(getFormattedNumber(convertedValue))
                            : '',
                    seriesInfo: !isBubbleChart
                        ? this.AllChartSeries[valueIndex]
                        : this.AllChartSeries[Math.floor(valueIndex / 2)],
                    ...value,
                };
            });
        });

        return chartTableData;
    }

    public updateChartSDKData(): void {
        this.chartData.forEach((subChartData: ISubChartModel) => {
            // 在修改数据后，将series的"展示数据长度"重置成新的，否则新添加的数据将不会展示
            subChartData.updateSeriesWithNewEditValue();
            const seriesSdkData = subChartData.getSeriesSdkData();
            subChartData.replaceSeries(seriesSdkData);
        });

        // 上报打开图表已修改数据
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.CHART,
            action: TDWActionType.CHART_EDITED,
        });
    }

    // columnIndex 是表格的列索引，从 1 开始
    public getEditingSubChartIndexes(columnIndex: number): {
        currentSubChartIndex: number;
        currentSubChartSeriesIndex: number;
        sumSeriesLength: number;
    } {
        let currentSubChartIndex = 0;
        let currentSubChartSeriesIndex = 0;
        let sumSeriesLength = 0;

        for (let index = 0; index < this.subChartSeriesLength.length; index++) {
            const currentSubChartSeriesLength = this.subChartSeriesLength[index];

            // 当前列索引在该字表的范围内
            if (columnIndex <= sumSeriesLength + currentSubChartSeriesLength) {
                currentSubChartSeriesIndex = columnIndex - sumSeriesLength - 1;
                break;
            }

            // 列号超出了初始加载时所有列的总和，直接返回最后一个字表的索引和计算后的子表里 series 对应的索引
            if (index === this.subChartSeriesLength.length - 1 && columnIndex > sumSeriesLength) {
                return {
                    currentSubChartIndex,
                    currentSubChartSeriesIndex: columnIndex - sumSeriesLength - 1,
                    sumSeriesLength,
                };
            }

            sumSeriesLength = sumSeriesLength + currentSubChartSeriesLength;
            currentSubChartIndex = currentSubChartIndex + 1;
        }

        return {
            currentSubChartIndex,
            currentSubChartSeriesIndex,
            sumSeriesLength,
        };
    }

    public openChartDataEditor(model): void {
        this.addChartModel(model);

        if (this.isShowChartEditModal) {
            this.spreadsheetInstance?.loadData([
                {
                    rows: this.chartTableData,
                },
            ]);
        } else {
            this.switchShowChartEditModal(true);

            // 上报打开图表编辑面板
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.CHART,
                action: TDWActionType.OPEN_CHART_EDIT_PANEL,
            });
        }
    }
}
